import type { UIEvent } from 'react'
import { useLayoutEffect, useRef } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'

export function useAppShellOverflowScroll() {
  const { key: locationKey, pathname } = useLocation()
  const navigationType = useNavigationType()
  const contentRef = useRef<HTMLDivElement>(null)
  const scrollTopByLocationKeyRef = useRef(new Map<string, number>())
  const lastPathnameRef = useRef(pathname)

  // AppShellContent never unmounts; React Router only resets window scroll.
  // https://github.com/remix-run/react-router/discussions/9495
  useLayoutEffect(() => {
    const savedScrollTop = scrollTopByLocationKeyRef.current.get(locationKey)
    const pathnameChanged = lastPathnameRef.current !== pathname
    lastPathnameRef.current = pathname

    if (navigationType === 'POP' && savedScrollTop !== undefined) {
      contentRef.current?.scrollTo(0, savedScrollTop)
      return
    }

    if (pathnameChanged) {
      contentRef.current?.scrollTo(0, 0)
    }

    scrollTopByLocationKeyRef.current.set(
      locationKey,
      contentRef.current?.scrollTop ?? 0,
    )
  }, [locationKey, navigationType, pathname])

  return {
    contentRef,
    onScroll(event: UIEvent<HTMLDivElement>) {
      scrollTopByLocationKeyRef.current.set(
        locationKey,
        event.currentTarget.scrollTop,
      )
    },
  }
}
